// filepath: cryptobag-com/src/App.jsx
import React from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, NavLink } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Page Imports
import Home from "./pages/Home";
import Prices from "./pages/Prices";
import Articles from "./pages/Articles";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Portfolio from "./pages/Portfolio";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";

// Component Imports
import Header from "./components/Header";
import CoinDetailModal from "./components/CoinDetailModal";
import MeshBackground from "./components/MeshBackground";

/**
 * Wrapper for the Coin Detail Modal to handle routing logic.
 * When the modal closes, the user is returned to the /prices view.
 */
function CoinModalWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleClose = () => navigate("/prices");

  return <CoinDetailModal coinId={id} onClose={handleClose} />;
}

export default function App() {
  const footerLinkClass = "text-sm text-gray-400 hover:text-white transition-colors";

  return (
    <HelmetProvider>
      <BrowserRouter>
        {/* MAIN WRAPPER 
          - relative: allows MeshBackground to use absolute/fixed positioning.
          - bg-gray-900: base color before the mesh loads.
        */}
        <div className="relative min-h-screen bg-gray-900 text-white font-['Inter',_sans-serif]">
          
          {/* MESH BACKGROUND
            - Placed at the top level with -z-10 to stay behind all content.
          */}
          <MeshBackground />

          {/* CONTENT LAYER
            - relative z-10: ensures this sits above the MeshBackground.
            - flex-col: allows the footer to stay at the bottom.
          */}
          <div className="relative z-10 flex flex-col min-h-screen">
            <Header />

            {/* CENTRALIZED LAYOUT CONTAINER
              - max-w-7xl: caps the width for large monitors.
              - mx-auto: centers the content.
              - px-4/sm:px-6/lg:px-8: Standardizes side-spacing for Home, Prices, and Articles.
            */}
            <main className="flex-grow w-full">
              <Routes>
                {/* 1. HOME PAGE */}
                <Route path="/" element={<Home />} />

                {/* 2. PRICES DASHBOARD */}
                <Route
                  path="/prices"
                  element={
                    <Prices />
                  }
                />

                {/* 3. DYNAMIC COIN MODAL ROUTE */}
                <Route
                  path="/coin/:id"
                  element={
                    <div>
                      <Prices />
                      <CoinModalWrapper />
                    </div>
                  }
                />

                {/* 4. CONTENT PAGES */}
                <Route path="/articles" element={<Articles />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />

                {/* 5. NOT FOUND */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>

            {/* FOOTER */}
            <footer className="mt-12 border-t border-gray-800/40 bg-gray-950/60 backdrop-blur-md">
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.2fr_0.8fr_1fr]">
                  <section className="space-y-3">
                    <div className="font-black text-2xl tracking-tighter text-white">
                      Crypto<span className="text-indigo-500">Bag</span>
                    </div>
                    <p className="max-w-md text-sm leading-5 text-gray-400">
                      A crypto information platform built around live market data, coin insights, curated news, and original content for people who want a clearer view of the crypto space.
                    </p>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500">
                      Track the market. Understand the space.
                    </p>
                  </section>

                  <section className="space-y-3">
                    <h2 className="text-xl font-black tracking-tight text-white">Explore</h2>
                    <nav className="flex flex-col items-start gap-1.5">
                      <NavLink to="/" className={footerLinkClass}>Home</NavLink>
                      <NavLink to="/prices" className={footerLinkClass}>Prices</NavLink>
                      <NavLink to="/articles" className={footerLinkClass}>Articles</NavLink>
                      <NavLink to="/portfolio" className={footerLinkClass}>Portfolio</NavLink>
                    </nav>
                  </section>

                  <section className="space-y-3">
                    <h2 className="text-xl font-black tracking-tight text-white">
                      Data <span className="text-indigo-500">Sources</span>
                    </h2>
                    <div className="space-y-2 text-sm leading-5 text-gray-400">
                      <p>
                        Market pricing and asset metadata from{" "}
                        <a
                          href="https://www.coingecko.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                          CoinGecko
                        </a>.
                      </p>
                      <p>
                        Sentiment data from{" "}
                        <a
                          href="https://alternative.me/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                          Alternative.me
                        </a>.
                      </p>
                      <p>News is aggregated from multiple providers and normalized into the CryptoBag feed.</p>
                    </div>
                  </section>
                </div>

                <div className="mt-10 flex flex-col gap-3 border-t border-gray-800/50 pt-5 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                  <p>&copy; {new Date().getFullYear()} CryptoBag. All rights reserved.</p>
                  <nav className="flex flex-wrap gap-x-4 gap-y-2">
                    <NavLink to="/about" className="hover:text-white transition-colors">About</NavLink>
                    <NavLink to="/contact" className="hover:text-white transition-colors">Contact</NavLink>
                    <NavLink to="/privacy" className="hover:text-white transition-colors">Privacy</NavLink>
                    <NavLink to="/terms" className="hover:text-white transition-colors">Terms</NavLink>
                  </nav>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}
